/**
 * 繰り返し支払いの自動処理(2026-09-07 指示)。
 *
 * Cloud Functions が使えないため、経理・承認者がアプリを開いているときに実行する:
 *   1. 当月〜翌々月の各回を「支払い待ち」として作る(作成済みはスキップ。PLの見込みとCFの支払予定に載る)
 *   2. 支払日が近い回(設定: 支払日のN日前・既定1日前)を経理へお知らせする(同じ回・同じ支払日には一度だけ)
 *      振込 → 経理が振り込んで支払い済みに / 口座引落 → 経理が引き落としを確認して確認済みに → いずれも承認者の完了確認(2026-09-08 指示)
 * 設定(マスタ管理→設定)で自動処理を止めることもできる。
 *
 * 2026-09-08: 「登録した直後に支払い待ちもお知らせも出ない」指摘を受け、画面を開いたとき1回だけ → 30分ごと・タブに戻ったとき・
 *             繰り返し支払いの登録/変更の直後(force)にも動くようにした。
 */
import { collection, doc, getDoc, getDocs, limit, query, serverTimestamp, setDoc, where, writeBatch } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { addDays, currentMonth, todayStr, toShortDate } from '@core/lib/date';
import { formatYen } from '@core/lib/money';
import { markActionDone, notifyUsers } from '@core/flow/notificationsRepo';
import { usersForNotification } from '@core/flow/applicationRepo';
import { fetchRecurringSettings } from '@core/masters/masterRepo';
import { PAYMENT_METHOD_LABELS, type CashAccount, type Transaction } from '@core/lib/types';
import { fetchCashAccounts } from '@core/cash/cashRepo';
import { fetchRecurringCosts } from './recurringRepo';
import { buildRecurringPlan, executeRecurringPlan } from './postRecurring';
import { nextMonthOf } from './schedule';

export type HousekeepingResult = {
  /** 新しく作った支払い待ちの件数 */
  created: number;
  /** 経理へ送ったお知らせの件数(回ごと) */
  notified: number;
};

/** 同じ利用者では30分に1回まで(force のときは常に実行) */
const MIN_INTERVAL_MS = 30 * 60 * 1000;
let lastRun: { userId: string; at: number } | null = null;
let inFlight: Promise<HousekeepingResult | null> | null = null;

/**
 * 支払い待ちの作成とお知らせ。
 * - 通常(force なし): 30分に1回まで。失敗しても画面を止めない(console.warn のみ)
 * - force: 登録・変更の直後に呼ぶ。必ず実行し、失敗は例外で返す(画面側でお知らせする)
 * 戻り値 null は「自動処理がOFF」または「間隔内のためスキップ」
 */
export async function runRecurringHousekeeping(params: {
  userId: string;
  fiscalStartMonth: number;
  force?: boolean;
}): Promise<HousekeepingResult | null> {
  if (inFlight) {
    if (!params.force) return inFlight;
    await inFlight.catch(() => null);
  }
  if (!params.force && lastRun && lastRun.userId === params.userId && Date.now() - lastRun.at < MIN_INTERVAL_MS) {
    return null;
  }
  lastRun = { userId: params.userId, at: Date.now() };
  inFlight = (async () => {
    try {
      const settings = await fetchRecurringSettings();
      if (!settings.auto_enabled) return null;

      let created = 0;
      const costs = await fetchRecurringCosts(false);
      if (costs.length > 0) {
        const accounts = await fetchCashAccounts().catch(() => [] as CashAccount[]);
        // 支払う月の 当月・翌月・翌々月 まで作る(計上日が「支払日の前々月」でも、その月のPL見込みに間に合うように)
        const thisMonth = currentMonth();
        const m1 = nextMonthOf(thisMonth);
        for (const month of [thisMonth, m1, nextMonthOf(m1)]) {
          const plan = await buildRecurringPlan({ month, costs, accounts });
          if (plan.createCount > 0) {
            const r = await executeRecurringPlan({ plan, userId: params.userId, fiscalStartMonth: params.fiscalStartMonth });
            created += r.created;
          }
        }
      }
      await clearStaleNotices(settings.notice_days_before);
      const notified = await sendDueNotices(settings.notice_days_before);
      return { created, notified };
    } catch (e) {
      if (params.force) throw e;
      // 自動処理の失敗で画面を止めない(次回に再試行される)
      console.warn('繰り返し支払いの自動処理に失敗しました', e);
      return null;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/** 支払日が近い「支払い待ち」を経理へ知らせる。recurring_notices に印を残して二重送信を防ぐ */
async function sendDueNotices(daysBefore: number): Promise<number> {
  const db = getDb();
  const today = todayStr();
  const limitDate = addDays(today, Math.max(0, daysBefore));
  const snap = await getDocs(
    query(
      collection(db, 'transactions'),
      where('source', '==', 'recurring'),
      where('status', '==', 'approved'),
      limit(200),
    ),
  );
  const due = snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as object) }) as Transaction)
    .filter((t) => t.payment_date <= limitDate);
  if (due.length === 0) return 0;

  const accounting = await usersForNotification('accounting');
  if (accounting.length === 0) return 0;

  let sent = 0;
  for (const t of due) {
    // お知らせ済みの印は「取引ID_支払日」。支払日を変えたら改めて知らせる。
    // 旧形式(取引IDのみ・2026-09-08 以前)は、同じ支払日なら済みとみなす
    const ref = doc(db, 'recurring_notices', `${t.id}_${t.payment_date}`);
    if ((await getDoc(ref)).exists()) continue;
    const legacy = await getDoc(doc(db, 'recurring_notices', t.id));
    if (legacy.exists() && (legacy.data() as { payment_date?: string }).payment_date === t.payment_date) continue;

    // 同じ回に前のお知らせ(支払日を変える前のもの等)が残っていれば済みにしてから送る。バッジが増え続けないように
    await markActionDone(t.id);
    await notifyUsers({
      userIds: accounting,
      transactionId: t.id,
      message: dueMessage(t, today),
      kind: 'action',
      stage: 'pay',
    });
    await setDoc(ref, {
      transaction_id: t.id,
      payment_date: t.payment_date,
      days_before: daysBefore,
      notified_at: serverTimestamp(),
    });
    sent++;
  }
  return sent;
}

/**
 * 期日でなくなった繰り返し支払いの「支払い」依頼を片付ける(2026-09-08 指摘: 支払日を先に変えても支払いのバッジが減らない)。
 * 未読の支払い依頼のうち、繰り返し支払いで「支払い待ちでない」「支払日がまだ先(表示・お知らせの対象外)」のものを既読にする
 */
async function clearStaleNotices(daysBefore: number): Promise<void> {
  const db = getDb();
  const limitDate = addDays(todayStr(), Math.max(0, daysBefore));
  const snap = await getDocs(
    query(
      collection(db, 'notifications'),
      where('kind', '==', 'action'),
      where('stage', '==', 'pay'),
      where('read', '==', false),
      limit(300),
    ),
  );
  const txIds = [...new Set(snap.docs.map((d) => (d.data() as { transaction_id?: string }).transaction_id).filter(Boolean))] as string[];
  const stale = new Set<string>();
  for (const id of txIds) {
    if (!id.startsWith('rc_')) continue; // 繰り返し支払い以外は触らない
    const tx = await getDoc(doc(db, 'transactions', id));
    const t = tx.exists() ? (tx.data() as Transaction) : null;
    if (!t || t.status !== 'approved' || t.payment_date > limitDate) stale.add(id);
  }
  // 同じ人・同じ回に複数残っていたら最新の1件だけ残す(支払日を何度か変えたときに増えた分)
  const newestByKey = new Map<string, number>();
  const rows = snap.docs.map((d) => {
    const x = d.data() as { user_id?: string; transaction_id?: string; created_at?: { toMillis?: () => number } };
    const at = x.created_at?.toMillis?.() ?? 0;
    const key = `${x.user_id ?? ''}|${x.transaction_id ?? ''}`;
    if ((newestByKey.get(key) ?? -1) < at) newestByKey.set(key, at);
    return { ref: d.ref, id: x.transaction_id ?? '', key, at };
  });
  const batch = writeBatch(db);
  let n = 0;
  for (const r of rows) {
    const isStale = r.id.startsWith('rc_') && stale.has(r.id);
    const isDuplicate = r.id.startsWith('rc_') && newestByKey.get(r.key) !== r.at;
    if (isStale || isDuplicate) {
      batch.update(r.ref, { read: true });
      n++;
    }
  }
  if (n > 0) await batch.commit();
  // お知らせ欄の同じ行(info・stage あり)も済みにする
  for (const id of stale) await markActionDone(id);
}

/** お知らせの文面。振込と口座引落で、やることが違う */
function dueMessage(t: Transaction, today: string): string {
  const name = `「${t.description ?? t.code}」`;
  const amount = `${formatYen(t.amount)}円`;
  const date = toShortDate(t.payment_date);
  const tomorrow = addDays(today, 1);
  if (t.payment_method === 'direct_debit' || t.payment_method === 'card') {
    const from = t.payment_method === 'card' ? 'カードの利用分が' : '口座から';
    const lead =
      t.payment_date === today
        ? `本日、${name}${t.payment_method === 'card' ? 'の' : 'が'}${from}引き落とされます`
        : t.payment_date < today
          ? `${name}の引き落とし日 ${date} を過ぎています`
          : t.payment_date === tomorrow
            ? `明日 ${date} に${name}${t.payment_method === 'card' ? 'の' : 'が'}${from}引き落とされます`
            : `${date} に${name}${t.payment_method === 'card' ? 'の' : 'が'}${from}引き落とされます`;
    const how = t.payment_method === 'card' ? 'クレジットカード' : '口座引落';
    return `【繰り返し支払い】${lead}(${amount}・${how})。引き落としを確認したら「支払い待ち」画面の繰り返し支払いから引き落とし確認済みにしてください`;
  }
  const method = t.payment_method ? PAYMENT_METHOD_LABELS[t.payment_method] : '';
  const lead =
    t.payment_date === today
      ? `本日は${name}の支払日です`
      : t.payment_date < today
        ? `${name}の支払日 ${date} を過ぎています`
        : t.payment_date === tomorrow
          ? `明日 ${date} は${name}の支払日です`
          : `${date} は${name}の支払日です`;
  return `【繰り返し支払い】${lead}(${amount}${method ? `・${method}` : ''})。支払ったら「支払い待ち」画面の繰り返し支払いから支払い詳細を入力してください`;
}
